import { useCallback, useEffect, useMemo, useState } from "react";

import { Menu } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { AgentService } from "services";
import { UserResponse } from "types";
import { AgentStatusUtils } from "utils";

import classes from "./AgentStatusMenu.module.css";
import useStartBreak from "./useStartBreak";

interface AgentStatusMenuProps {
  user: UserResponse;
}

const AgentStatusMenu = ({ user }: AgentStatusMenuProps) => {
  const { t } = useTranslation();
  const { startBreak, startingBreak } = useStartBreak();
  const [serverStatus, setServerStatus] = useState<string>("ACTIVE");
  const [status, setStatus] = useState<AgentStatusUtils.AgentStatusValue>(
    AgentStatusUtils.getDerivedAgentStatus("ACTIVE"),
  );

  const syncDerivedStatus = useCallback((nextServerStatus?: string) => {
    setStatus(AgentStatusUtils.getDerivedAgentStatus(nextServerStatus ?? serverStatus));
  }, [serverStatus]);

  useEffect(() => {
    let mounted = true;

    const loadStatus = async () => {
      try {
        const response = await AgentService.agentStatus();
        if (!mounted) {
          return;
        }

        setServerStatus(response.status);
        setStatus(AgentStatusUtils.getDerivedAgentStatus(response.status));
      } catch {
        if (mounted) {
          syncDerivedStatus();
        }
      }
    };

    loadStatus();

    return () => {
      mounted = false;
    };
  }, [syncDerivedStatus]);

  useEffect(() => {
    if (status === "ON_BREAK") {
      return;
    }

    let timeoutId: number | undefined;

    const scheduleInactiveTransition = (timestamp?: number) => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      const lastActivityAt = timestamp ?? AgentStatusUtils.getLastAgentActivityAt() ?? Date.now();
      const remainingTime = AgentStatusUtils.AGENT_ACTIVITY_TIMEOUT_MS - (Date.now() - lastActivityAt);

      if (remainingTime <= 0) {
        setStatus("INACTIVE");
        return;
      }

      timeoutId = window.setTimeout(() => setStatus("INACTIVE"), remainingTime);
    };

    const handleActivity = (event: Event) => {
      const customEvent = event as CustomEvent<number>;
      setStatus("ACTIVE");
      scheduleInactiveTransition(customEvent.detail);
    };

    scheduleInactiveTransition();
    window.addEventListener(AgentStatusUtils.AGENT_ACTIVITY_EVENT, handleActivity as EventListener);

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      window.removeEventListener(AgentStatusUtils.AGENT_ACTIVITY_EVENT, handleActivity as EventListener);
    };
  }, [status]);

  const statusColorClass = useMemo(() => {
    if (status === "INACTIVE") {
      return classes.statusInactive;
    }

    if (status === "ON_BREAK") {
      return classes.statusOnBreak;
    }

    return classes.statusActive;
  }, [status]);

  const statusLabel = useMemo(() => {
    if (status === "INACTIVE") {
      return t("agentStatus.inactive");
    }

    if (status === "ON_BREAK") {
      return t("agentStatus.onBreak");
    }

    return t("agentStatus.active");
  }, [status, t]);

  return (
    <Menu shadow={"md"} width={220} position={"bottom-end"} withArrow={true}>
      <Menu.Target>
        <button className={classes.statusButton} type={"button"}>
          <Text className={classes.name} size={"md"}>{user.fullName}</Text>
          <div className={`${classes.statusRow} ${statusColorClass}`}>
            <span className={classes.statusText}>{statusLabel}</span>
            <IconChevronDown className={classes.statusChevron} size={14} stroke={1.75} />
          </div>
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item disabled={startingBreak} onClick={startBreak}>
          {t("agentStatus.startBreak")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default AgentStatusMenu;
